import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "font-medium text-gray-600" }
const _hoisted_2 = { class: "w-full text-sm text-gray-500" }
const _hoisted_3 = {
  key: 0,
  class: "sm:col-span-12"
}
const _hoisted_4 = { class: "mb-2 block text-sm font-medium text-gray-800" }
const _hoisted_5 = { class: "mt-4 flex flex-col justify-end pb-1.5" }
const _hoisted_6 = { class: "mt-6 grid grid-cols-2 place-content-around gap-4 text-center" }

import { onMounted, ref, Ref, computed, WritableComputedRef, PropType } from 'vue';
import {
    ConnectFlowModelingActions,
    ConnectFlowModelingGetters,
    ConnectFlowModelingMutations,
    VueFlowNodeModel,
    MultiSelectOption,
} from '@/types';
import store from '@/store';
import { ReplyIcon, PlusIcon } from '@heroicons/vue/outline';

import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { useI18n } from 'vue-i18n';
import { v4 as uuidv4 } from 'uuid';
import TransferToExternalNumberField from '@/views/pages/connect-flow/components/flow/nodes/FormElements/TransferToExternalNumberField.vue';
import { requiredIf } from '@/utils/validators';

interface IExternalNumber {
    id: string;
    phone: string;
}

interface IExternalFormState {
    numbers: IExternalNumber[];
    redirectType: undefined | string;
    whisper: null | string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TransferToExternal',
  props: {
    node: {
        type: Object as PropType<VueFlowNodeModel>,
        required: true,
    },
},
  setup(__props) {

const { t } = useI18n();

const props = __props;

const showForm = ref(false);

const nodeWritable: WritableComputedRef<VueFlowNodeModel> = computed({
    get: () => store.getters['flow/' + ConnectFlowModelingGetters.ELEMENT_BY_ID](props.node.id),
    set: (value: VueFlowNodeModel) => {
        store.commit('flow/' + ConnectFlowModelingMutations.UPDATE_ELEMENT, {
            idx: null,
            value,
        });
    },
});

const formState: Ref<IExternalFormState> = ref({
    numbers: [],
    redirectType: undefined,
    whisper: null,
});

const rules = computed<ValidationArgs<IExternalFormState>>(() => {
    const r: ValidationArgs<IExternalFormState> = {
        numbers: {},
        redirectType: {
            required: requiredIf(() => {
                return formState.value.numbers.length > 1;
            }),
        },
        whisper: {},
    };
    return r;
});
const v$: Ref<Validation<ValidationArgs<IExternalFormState>>> = useVuelidate(rules, formState);

const CallingRedirectType = {
    SEQUENTIAL: 'SEQUENTIAL',
    SIMULTANEOUS: 'SIMULTANEOUS',
};

const optionsType: Ref<MultiSelectOption[]> = ref([
    {
        label: t('connectFlow.node.transferToExternal.types.sequential'),
        value: CallingRedirectType.SEQUENTIAL,
    },
    {
        label: t('connectFlow.node.transferToExternal.types.simultaneous'),
        value: CallingRedirectType.SIMULTANEOUS,
    },
]);

const nodeText = computed(() => {
    let text = t('connectFlow.node.transferToExternal.empty');
    if (nodeWritable.value.data.callTo !== undefined) {
        text = t('connectFlow.node.transferToExternal.callTo', {
            number: nodeWritable.value.data.callTo.join('; '),
        });
    }
    return text;
});

const addNumber = () => {
    formState.value.numbers.push({
        id: uuidv4(),
        phone: '',
    });
};
const removeNumber = (index: number) => {
    formState.value.numbers.splice(index, 1);
};

const isFormCorrect = ref(true);

function removeNode() {
    store.dispatch('flow/' + ConnectFlowModelingActions.DELETE_NODE, {
        nodeId: nodeWritable.value.id,
        parentId: nodeWritable.value.parentId,
    });
}

async function onSubmit() {
    isFormCorrect.value = await v$.value.$validate();
    if (isFormCorrect.value && formState.value.numbers.length) {
        nodeWritable.value.data.callTo = formState.value.numbers.map((item) => item.phone);
        nodeWritable.value.data.redirectType = formState.value.redirectType ?? undefined;
        nodeWritable.value.data.whisper = formState.value.whisper ?? undefined;

        if (formState.value.numbers.length === 1) {
            nodeWritable.value.data.redirectType = undefined;
        }

        showForm.value = false;
    }
    nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    v$.value.$reset();
}

onMounted(() => {
    if (nodeWritable.value.data.callTo !== undefined) {
        formState.value.numbers = nodeWritable.value.data.callTo.map((item) => {
            return { id: uuidv4(), phone: item };
        });
    }
    formState.value.redirectType = nodeWritable.value.data.redirectType;
    if (nodeWritable.value.data.whisper !== undefined) {
        formState.value.whisper = nodeWritable.value.data.whisper;
    }

    if (formState.value.numbers.length === 0) {
        formState.value.numbers.push({ id: uuidv4(), phone: '' });
    }

    v$.value.$validate().then((result) => {
        isFormCorrect.value = result;
        nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    });
});

return (_ctx: any,_cache: any) => {
  const _component_UiNode = _resolveComponent("UiNode")!
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiMultiselect = _resolveComponent("UiMultiselect")!
  const _component_UiTextArea = _resolveComponent("UiTextArea")!
  const _component_UiRightPanel = _resolveComponent("UiRightPanel")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_UiNode, {
      nodeId: __props.node.id,
      title: _ctx.$t('connectFlow.node.transferToExternal.title'),
      icon: _unref(ReplyIcon),
      showActions: "",
      onEditNode: _cache[0] || (_cache[0] = ($event: any) => (showForm.value = true)),
      onRemoveNode: removeNode,
      class: _normalizeClass(isFormCorrect.value ? '' : 'border-red-300 shadow-red-300')
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("span", null, _toDisplayString(nodeText.value), 1)
        ])
      ]),
      _: 1
    }, 8, ["nodeId", "title", "icon", "class"]),
    _createVNode(_component_UiRightPanel, {
      modelValue: showForm.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((showForm).value = $event)),
      title: _ctx.$t('connectFlow.node.transferToExternal.title'),
      icon: _unref(ReplyIcon)
    }, {
      default: _withCtx(() => [
        _createElementVNode("form", {
          autocomplete: "off",
          class: "flex flex-col gap-3",
          onSubmit: _withModifiers(onSubmit, ["prevent"])
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(formState.value.numbers, (number, idx) => {
            return (_openBlock(), _createBlock(TransferToExternalNumberField, {
              key: `${__props.node.id}_number_${number.id}`,
              modelValue: formState.value.numbers[idx].phone,
              "onUpdate:modelValue": ($event: any) => ((formState.value.numbers[idx].phone) = $event),
              onRemove: ($event: any) => (removeNumber(idx)),
              removable: formState.value.numbers.length > 1
            }, null, 8, ["modelValue", "onUpdate:modelValue", "onRemove", "removable"]))
          }), 128)),
          _createVNode(_component_UiButton, {
            class: "mt-6 flex items-center text-lg",
            variant: "white",
            size: "md",
            onClick: addNumber
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(PlusIcon), { class: "h-4 w-4 text-gray-500" }),
              _createElementVNode("span", _hoisted_2, _toDisplayString(_ctx.$t('connectFlow.node.transferToExternal.addNumber')), 1)
            ]),
            _: 1
          }),
          (formState.value.numbers.length > 1)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createElementVNode("label", _hoisted_4, _toDisplayString(_ctx.$t('connectFlow.node.transferToExternal.type')), 1),
                _createVNode(_component_UiMultiselect, {
                  name: "audio",
                  modelValue: formState.value.redirectType,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formState.value.redirectType) = $event)),
                  options: optionsType.value,
                  errors: _unref(v$).redirectType.$silentErrors,
                  "can-clear": "",
                  primitive: ""
                }, null, 8, ["modelValue", "options", "errors"])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_component_UiTextArea, {
              label: _ctx.$t('connectFlow.node.transferToExternal.whisper'),
              name: "whisper",
              modelValue: formState.value.whisper,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formState.value.whisper) = $event))
            }, null, 8, ["label", "modelValue"])
          ]),
          _createElementVNode("div", _hoisted_6, [
            _createVNode(_component_UiButton, {
              variant: "white",
              "text-variant": "gray-700",
              onClick: _cache[3] || (_cache[3] = ($event: any) => (showForm.value = false))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Cancel')), 1)
              ]),
              _: 1
            }),
            _createVNode(_component_UiButton, {
              type: "submit",
              variant: "primary",
              "text-variant": "white"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Apply')), 1)
              ]),
              _: 1
            })
          ])
        ], 32)
      ]),
      _: 1
    }, 8, ["modelValue", "title", "icon"])
  ], 64))
}
}

})